// client/src/pages/AnnouncementsPage.js

import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Snackbar,
  IconButton,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Switch,
  OutlinedInput
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";
import { ROLES } from "../../config/pagePermissions";
import { fetchJson } from "../../utils/fetchJson";
import { UserContext } from "../../contexts/UserContext";

export default function AnnouncementsPage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);

  const canManage = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('communications');
  }, [user]);

  const [anns, setAnns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [schools, setSchools] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalSent: 0,
    totalRead: 0,
    readRate: 0,
    engagementBySchool: [],
    recentActivity: []
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formValues, setFormValues] = useState({
    title: "",
    message: "",
    target: "All",
    priority: "normal",
    scheduledAt: "",
    expiresAt: "",
    channels: {
      email: true,
      sms: false,
      portal: true
    },
    targetSchools: [],
    targetRoles: [],
    isScheduled: false,
    isRecurring: false,
    recurringPattern: "once"
  });

  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [attachments, setAttachments] = useState([]); // [{url, filename, size, mime}]
  const MAX_FILES = 5;
  const MAX_SIZE = 10 * 1024 * 1024; // 10MB
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    loadAnnouncements();
    loadSchools();
    loadAnalytics();
  }, []);

  async function loadAnnouncements() {
    setLoading(true);
    setError("");
    try {
      const list = await fetchJson("/api/announcements?scope=system", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`
        }
      });
      setAnns(list.announcements || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadSchools() {
    try {
      const data = await fetchJson("/schools");
      setSchools(data.schools || data || []);
    } catch (err) {
      console.error("Failed to load schools:", err);
    }
  }

  async function loadAnalytics() {
    try {
      const data = await fetchJson("/api/announcements/analytics");
      setAnalytics(data);
    } catch (err) {
      console.error("Failed to load analytics:", err);
    }
  }

  function openDialog(announcement = null) {
    if (!canManage) return;
    if (announcement) {
      setEditingAnnouncement(announcement);
      setFormValues({
        title: announcement.title,
        message: announcement.message,
        target: announcement.target || "All",
        priority: announcement.priority || "normal",
        scheduledAt: announcement.scheduledAt || "",
        expiresAt: announcement.expiresAt || "",
        channels: { ...(announcement.channels || { email: true, sms: false }), portal: true },
        targetSchools: announcement.targetSchools || [],
        targetRoles: announcement.targetRoles || [],
        isScheduled: announcement.isScheduled || false,
        isRecurring: announcement.isRecurring || false,
        recurringPattern: announcement.recurringPattern || "once"
      });
    } else {
      setEditingAnnouncement(null);
      setFormValues({
        title: "",
        message: "",
        target: "All",
        priority: "normal",
        scheduledAt: "",
        expiresAt: "",
        channels: { email: true, sms: false, portal: true },
        targetSchools: [],
        targetRoles: [],
        isScheduled: false,
        isRecurring: false,
        recurringPattern: "once"
      });
    }
    setError("");
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setError("");
    setEditingAnnouncement(null);
  }

  async function handleCreate() {
    if (!canManage) return;
    const { title, message, target, priority, scheduledAt, expiresAt, channels, targetSchools, targetRoles, isScheduled, isRecurring, recurringPattern } = formValues;
    
    if (!title.trim() || !message.trim()) {
      setError(t("announcementsPage.fillFields") || "Please fill in both title and message");
      return;
    }

    try {
      // Map to backend payload for role-targeted announcements
      const payload = {
        title,
        body: message,
        roles: (target === 'Custom' && Array.isArray(targetRoles) && targetRoles.length) ? targetRoles :
               (target === 'Students' ? ['Student'] :
               target === 'Teachers' ? ['Teacher'] :
               target === 'Parents' ? ['Parent'] :
               target === 'SchoolAdmins' ? ['SchoolAdmin'] : ['Student','Teacher','Parent','SchoolAdmin','Principal','HeadMaster','FinanceOfficer','Librarian','TransportOfficer']),
        attachments: attachments.map(a => a.url)
      };

      if (scheduledAt) payload.scheduledAt = scheduledAt;
      if (expiresAt) payload.expiresAt = expiresAt;

      if (editingAnnouncement) {
        await fetchJson(`/api/announcements/${editingAnnouncement.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`
          },
          body: JSON.stringify(payload)
        });
        setSnackbarMsg(t("announcementsPage.updated") || "Announcement updated");
      } else {
        await fetchJson("/api/announcements", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`
          },
          body: JSON.stringify(payload)
        });
        setSnackbarMsg(t("announcementsPage.created") || "Announcement created");
      }
      
      setSnackbarOpen(true);
      closeDialog();
      loadAnnouncements();
    } catch (err) {
      setError(err.message);
    }
  }

  async function onUpload(e) {
    if (!canManage) return;
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (attachments.length + files.length > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} attachments allowed`);
      return;
    }
    for (const f of files) {
      if (f.size > MAX_SIZE) {
        setError(`File ${f.name} exceeds 10MB limit`);
        return;
      }
    }
    try {
      const uploaded = [];
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        const data = await fetchJson('/api/messages/attachment', { method: 'POST', body: form });
        uploaded.push(data);
      }
      setAttachments(prev => [...prev, ...uploaded]);
      e.target.value = '';
    } catch (err) {
      setError(err?.message || 'Upload failed');
    }
  }

  async function handleDelete(id) {
    if (!canManage) return;
    try {
      await fetchJson(`/api/announcements/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`
        }
      });
      setSnackbarMsg(t("announcementsPage.deleted") || "Announcement deleted");
      setSnackbarOpen(true);
      loadAnnouncements();
    } catch (err) {
      setError(err.message);
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'primary';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'success';
      case 'scheduled': return 'info';
      case 'draft': return 'default';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          System-wide Announcements
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => openDialog()}
          disabled={!canManage}
        >
          Create Announcement
        </Button>
      </Box>

      {/* Analytics Summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {analytics.totalSent}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Announcements Sent
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">
                {analytics.totalRead}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Read
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="info.main">
                {analytics.readRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Read Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">
                {anns.filter(a => a.status === 'scheduled').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Scheduled
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="All Announcements" />
        <Tab label="Scheduled" />
        <Tab label="Analytics" />
      </Tabs>

      {/* All Announcements Tab */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Target Roles</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Channels</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {anns.map((ann) => (
                  <TableRow key={ann.id}>
                    <TableCell>
                      <Typography variant="subtitle2">{ann.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(ann.body || ann.message || '').substring(0, 100)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={(Array.isArray(ann.roles) ? ann.roles.join(', ') : (ann.target || 'All'))} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={ann.priority || 'normal'} 
                        color={getPriorityColor(ann.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {ann.channels ? (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {ann.channels?.email && <Chip label="Email" size="small" />}
                          {ann.channels?.sms && <Chip label="SMS" size="small" />}
                          {ann.channels?.app && <Chip label="App" size="small" />}
                          {ann.channels?.portal && <Chip label="Portal" size="small" />}
                        </Box>
                      ) : <Typography variant="caption" color="text.secondary">N/A</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={ann.status || 'sent'} 
                        color={getStatusColor(ann.status || 'sent')}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(ann.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* Edit currently not supported for new backend stub */}
                        <Tooltip title="View Analytics">
                          <IconButton size="small">
                            <AnalyticsIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(ann.id)} disabled={!canManage}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Scheduled Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Scheduled Announcements</Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Scheduled For</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {anns.filter(a => a.status === 'scheduled').map((ann) => (
                  <TableRow key={ann.id}>
                    <TableCell>{ann.title}</TableCell>
                    <TableCell>
                      {new Date(ann.scheduledAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip label={ann.target} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip label="Scheduled" color="info" size="small" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openDialog(ann)} disabled={!canManage}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" color="error" disabled={!canManage}>
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Analytics Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Engagement by School</Typography>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>School</TableCell>
                      <TableCell>Sent</TableCell>
                      <TableCell>Read</TableCell>
                      <TableCell>Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.engagementBySchool.map((school) => (
                      <TableRow key={school.id}>
                        <TableCell>{school.name}</TableCell>
                        <TableCell>{school.sent}</TableCell>
                        <TableCell>{school.read}</TableCell>
                        <TableCell>{school.rate}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Recent Activity</Typography>
                <List>
                  {analytics.recentActivity.map((activity, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={activity.action}
                        secondary={activity.time}
                      />
                      <Chip label={activity.type} size="small" />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAnnouncement ? "Edit Announcement" : "Create New Announcement"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formValues.title}
                onChange={(e) => setFormValues({ ...formValues, title: e.target.value })}
                disabled={!canManage}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Message"
                value={formValues.message}
                onChange={(e) => setFormValues({ ...formValues, message: e.target.value })}
                disabled={!canManage}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Target Audience</InputLabel>
                <Select
                  value={formValues.target}
                  onChange={(e) => setFormValues({ ...formValues, target: e.target.value })}
                  label="Target Audience"
                  disabled={!canManage}
                >
                  <MenuItem value="All">All Users</MenuItem>
                  <MenuItem value="Students">Students Only</MenuItem>
                  <MenuItem value="Teachers">Teachers Only</MenuItem>
                  <MenuItem value="Parents">Parents Only</MenuItem>
                  <MenuItem value="SchoolAdmins">School Admins Only</MenuItem>
                  <MenuItem value="Custom">Custom Selection</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formValues.priority}
                  onChange={(e) => setFormValues({ ...formValues, priority: e.target.value })}
                  label="Priority"
                  disabled={!canManage}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Delivery Channels</Typography>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.channels.email}
                      onChange={(e) => setFormValues({
                        ...formValues,
                        channels: { ...formValues.channels, email: e.target.checked }
                      })}
                      disabled={!canManage}
                    />
                  }
                  label="Email"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.channels.sms}
                      onChange={(e) => setFormValues({
                        ...formValues,
                        channels: { ...formValues.channels, sms: e.target.checked }
                      })}
                      disabled={!canManage}
                    />
                  }
                  label="SMS"
                />
                {/* "Mobile App" was a distinction without a difference: the
                    portal IS the app — the same announcement read on a phone
                    instead of a browser. Ticking one and not the other
                    described a delivery the system does not make.

                    Portal is now fixed on and shown as such rather than being
                    switchable: an announcement that appears nowhere in the
                    product is not an announcement, and email and SMS are the
                    extra pushes on top of it. */}
                <FormControlLabel
                  control={<Checkbox checked disabled />}
                  label="In the app and portal"
                />
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.isScheduled}
                    onChange={(e) => setFormValues({ ...formValues, isScheduled: e.target.checked })}
                    disabled={!canManage}
                  />
                }
                label="Schedule for later"
              />
            </Grid>
            {formValues.isScheduled && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Schedule Date & Time"
                    value={formValues.scheduledAt}
                    onChange={(e) => setFormValues({ ...formValues, scheduledAt: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    disabled={!canManage}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Expires At (Optional)"
                    value={formValues.expiresAt}
                    onChange={(e) => setFormValues({ ...formValues, expiresAt: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    disabled={!canManage}
                  />
                </Grid>
              </>
            )}
            {formValues.target === "Custom" && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Select Target Schools</Typography>
                <FormGroup row>
                  {schools.map((school) => (
                    <FormControlLabel
                      key={school.id}
                      control={
                        <Checkbox
                          checked={formValues.targetSchools.includes(school.id)}
                          onChange={(e) => {
                            const newSchools = e.target.checked
                              ? [...formValues.targetSchools, school.id]
                              : formValues.targetSchools.filter(id => id !== school.id);
                            setFormValues({ ...formValues, targetSchools: newSchools });
                          }}
                          disabled={!canManage}
                        />
                      }
                      label={school.name}
                    />
                  ))}
                </FormGroup>
              </Grid>
            )}
            {formValues.target === "Custom" && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="target-roles-label">Target Roles</InputLabel>
                  <Select
                    multiple
                    labelId="target-roles-label"
                    input={<OutlinedInput label="Target Roles" />}
                    value={formValues.targetRoles}
                    onChange={(e) => setFormValues({ ...formValues, targetRoles: e.target.value })}
                    renderValue={(selected) => selected.join(', ')}
                    disabled={!canManage}
                  >
                    {Object.values(ROLES).map((r) => (
                      <MenuItem key={r} value={r}>
                        <Checkbox checked={formValues.targetRoles.indexOf(r) > -1} />
                        <ListItemText primary={r} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Attachments uploader */}
            <Grid item xs={12}>
              <Button variant="outlined" component="label" disabled={!canManage}>Attach files
                <input hidden multiple type="file" onChange={onUpload} />
              </Button>
              <Typography variant="caption" sx={{ ml: 2 }} color="text.secondary">Max 5 files, up to 10MB each</Typography>
            </Grid>
            {attachments.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {attachments.map((a, idx) => (
                    <Chip key={`${a.url}-${idx}`} label={a.filename || a.url} onDelete={canManage ? () => setAttachments(prev => prev.filter((_, i) => i !== idx)) : undefined} clickable component="a" href={a.url} target="_blank" rel="noopener" />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!canManage}>
            {editingAnnouncement ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success">
          {snackbarMsg}
        </Alert>
      </Snackbar>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
